/**
 * ViewRenderer Component
 *
 * Renders the correct view component based on the active view ID.
 * Handles lazy loading, error boundaries, and placeholder views.
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import React, { Suspense, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Loader2 } from 'lucide-react';
import { getViewConfig, isPlaceholderView } from '../../config/viewRegistry';
import ViewErrorBoundary from './ViewErrorBoundary';
import PlaceholderView from './PlaceholderView';

/**
 * Loading spinner for lazy-loaded views
 */
function ViewLoadingFallback() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      <p className="mt-4 text-sm text-gray-500">Loading...</p>
    </div>
  );
}

/**
 * Unknown view fallback
 */
function UnknownView({ viewId }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      <div className="text-center max-w-md">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          View Not Found
        </h2>
        <p className="text-gray-500">
          The view "{viewId}" does not exist.
        </p>
      </div>
    </div>
  );
}

UnknownView.propTypes = {
  viewId: PropTypes.string,
};

/**
 * ViewRenderer renders the appropriate component for a view ID
 * @param {object} props
 * @param {string} props.viewId - The view ID to render
 * @param {object} props.user - User object for error boundary telemetry
 * @param {function} props.onNavigateLegacy - Legacy navigation handler for views
 * @param {object} props.viewProps - Additional props to pass to the view
 */
function ViewRenderer({ viewId, user, onNavigateLegacy, viewProps = {} }) {
  const viewConfig = useMemo(() => getViewConfig(viewId), [viewId]);

  // Unknown view
  if (!viewConfig) {
    return <UnknownView viewId={viewId} />;
  }

  // Action-only view (no component to render)
  if (viewConfig.action && !viewConfig.component) {
    // Actions are handled at the navigation level, not here
    return null;
  }

  // Placeholder view
  if (isPlaceholderView(viewId)) {
    return (
      <PlaceholderView
        title={viewConfig.title}
        description={viewConfig.placeholderDescription}
      />
    );
  }

  // Get component
  const ViewComponent = viewConfig.component;
  if (!ViewComponent) {
    return <UnknownView viewId={viewId} />;
  }

  // Build props for the view
  const componentProps = {
    ...viewProps,
    onNavigateLegacy,
  };

  // Add mode prop if specified in config (e.g., for ReportsDashboard)
  if (viewConfig.mode) {
    componentProps.mode = viewConfig.mode;
  }

  // Render with error boundary
  const content = (
    <ViewErrorBoundary
      viewId={viewId}
      viewTitle={viewConfig.title}
      user={user}
    >
      <ViewComponent {...componentProps} />
    </ViewErrorBoundary>
  );

  // Wrap lazy-loaded components with Suspense
  if (viewConfig.lazy) {
    return (
      <Suspense fallback={<ViewLoadingFallback />}>
        {content}
      </Suspense>
    );
  }

  return content;
}

ViewRenderer.propTypes = {
  viewId: PropTypes.string.isRequired,
  user: PropTypes.object,
  onNavigateLegacy: PropTypes.func,
  viewProps: PropTypes.object,
};

ViewRenderer.defaultProps = {
  user: null,
  onNavigateLegacy: null,
  viewProps: {},
};

export default ViewRenderer;
